// Iter 31.283 (Phase 2, 8 Jul 2026) — Roster Admin surface.
//
// Two's brief: list every row in db.roster_entries, search by
// name/number/role, filter by canonical type, inline-edit tags,
// promote-to-public toggle. Writes go through /api/admin/roster
// (JWT auth matches every other Shed admin surface). Every write
// files a hash-chained audit row on life_entries via the backend,
// so Canon Rule 1 holds.
import React, { useEffect, useState, useCallback } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Loader2, Search, ChevronLeft, ChevronRight, Check, X, Pencil } from "lucide-react";

const TYPE_OPTIONS = [
    { value: "", label: "All types" },
    { value: "operative", label: "Operative" },
    { value: "contact", label: "Contact" },
    { value: "vendor", label: "Vendor" },
    { value: "structural", label: "Structural" },
    { value: "closed", label: "Closed" },
];

const PROMOTED_OPTIONS = [
    { value: "", label: "Any" },
    { value: "true", label: "Promoted" },
    { value: "false", label: "Private" },
];

function TypePill({ value }) {
    const cls = {
        operative: "border-stamp-green text-stamp-green",
        contact: "border-ink/50 text-ink",
        vendor: "border-ink/30 text-ink-soft",
        structural: "border-stamp-red text-stamp-red",
        closed: "border-dashed border-ink/30 text-ink-soft",
    }[value] || "border-ink/30 text-ink-soft";
    return (
        <span
            className={`inline-block border px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${cls}`}
            data-testid={`roster-type-pill-${value}`}
        >
            {value || "—"}
        </span>
    );
}

function TagsCell({ entry, onSave }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState((entry.tags || []).join(", "));
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        setDraft((entry.tags || []).join(", "));
    }, [entry.tags]);

    const commit = async () => {
        setSaving(true);
        setErr("");
        const next = draft.split(",").map((s) => s.trim()).filter(Boolean);
        try {
            await onSave({ tags: next });
            setEditing(false);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <div
                className="flex flex-wrap items-center gap-1 cursor-pointer group min-h-[24px]"
                onClick={() => setEditing(true)}
                data-testid={`roster-tags-${entry.key}`}
            >
                {(entry.tags || []).length === 0 && (
                    <span className="label-typed text-ink-soft/60 italic">no tags</span>
                )}
                {(entry.tags || []).map((t) => (
                    <span
                        key={t}
                        className="inline-block border border-ink/25 bg-paper-light/50 px-1.5 py-0.5 font-body text-[11px] text-ink"
                    >
                        {t}
                    </span>
                ))}
                <Pencil className="w-3 h-3 text-ink-soft/40 group-hover:text-ink transition-opacity" />
            </div>
        );
    }
    return (
        <div className="flex items-start gap-1" data-testid={`roster-tags-editor-${entry.key}`}>
            <input
                type="text"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") commit();
                    if (e.key === "Escape") { setEditing(false); setDraft((entry.tags || []).join(", ")); }
                }}
                placeholder="tag one, tag two, tag three"
                className="w-full border border-ink/30 bg-paper-light px-2 py-1 font-body text-xs text-ink focus:outline-none focus:border-stamp-red"
                data-testid={`roster-tags-input-${entry.key}`}
                disabled={saving}
            />
            <button
                onClick={commit}
                disabled={saving}
                className="border border-stamp-green text-stamp-green px-1.5 py-1 hover:bg-stamp-green/10 disabled:opacity-50"
                data-testid={`roster-tags-save-${entry.key}`}
                title="Save (Enter)"
            >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            </button>
            <button
                onClick={() => { setEditing(false); setDraft((entry.tags || []).join(", ")); }}
                disabled={saving}
                className="border border-ink/30 text-ink-soft px-1.5 py-1 hover:bg-paper-light disabled:opacity-50"
                data-testid={`roster-tags-cancel-${entry.key}`}
                title="Cancel (Esc)"
            >
                <X className="w-3 h-3" />
            </button>
            {err && <span className="label-typed text-stamp-red ml-1">{err}</span>}
        </div>
    );
}

function PromoteToggle({ entry, onSave }) {
    const [saving, setSaving] = useState(false);
    const promoted = !!entry.promoted_to_public;
    const flip = async () => {
        setSaving(true);
        try {
            await onSave({ promoted_to_public: !promoted });
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="flex items-center gap-2" data-testid={`roster-promote-cell-${entry.key}`}>
            <Switch
                checked={promoted}
                onCheckedChange={flip}
                disabled={saving}
                data-testid={`roster-promote-switch-${entry.key}`}
            />
            <span className="label-typed text-[10px] uppercase tracking-[0.18em] text-ink-soft">
                {promoted ? "Public" : "Private"}
            </span>
            {saving && <Loader2 className="w-3 h-3 animate-spin text-ink-soft" />}
        </div>
    );
}

export default function AdminRoster() {
    const [rows, setRows] = useState([]);
    const [byType, setByType] = useState({});
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [q, setQ] = useState("");
    const [qDebounced, setQDebounced] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [promoted, setPromoted] = useState("");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // Debounce free-text query.
    useEffect(() => {
        const t = setTimeout(() => setQDebounced(q), 250);
        return () => clearTimeout(t);
    }, [q]);

    // Reset to page 1 whenever filters change.
    useEffect(() => {
        setPage(1);
    }, [qDebounced, typeFilter, promoted]);

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const params = {
                page,
                page_size: pageSize,
                include_closed: true,
            };
            if (qDebounced.trim()) params.q = qDebounced.trim();
            if (typeFilter) params.type = typeFilter;
            if (promoted) params.promoted = promoted;
            const { data } = await api.get("/admin/roster", { params });
            setRows(data.entries || []);
            setByType(data.by_type || {});
            setTotal(data.total || 0);
            setPages(data.pages || 0);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Failed to load roster");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, qDebounced, typeFilter, promoted]);

    useEffect(() => { load(); }, [load]);

    const patchRow = async (key, body) => {
        const { data } = await api.patch(`/admin/roster/${encodeURIComponent(key)}`, body);
        // Optimistic in-place update so the row doesn't flicker.
        setRows((cur) => cur.map((r) => (
            r.key === key
                ? { ...r, tags: data.tags, promoted_to_public: data.promoted_to_public }
                : r
        )));
    };

    return (
        <Shell>
            <div className="mb-6" data-testid="admin-roster-header">
                <div className="label-typed tw-prefix">Phase 2 · roster admin</div>
                <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                    Roster Admin<span className="cursor-blink" />
                </h1>
                <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                    Every row in <code className="font-body text-xs">db.roster_entries</code>.
                    Search by name / alias / role / key, filter by canonical type,
                    tag inline, and promote to the public Operators surface. Every
                    edit is filed as a hash-chained audit row on the ledger — Canon Rule 1.
                </p>
            </div>

            {/* Filter strip */}
            <div className="paper-card tape-corner p-4 mb-4" data-testid="admin-roster-filters">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-ink-soft" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search name / alias / role / key…"
                            className="w-full border border-ink/30 bg-paper-light pl-7 pr-3 py-1.5 font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                            data-testid="admin-roster-search-input"
                        />
                    </div>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="border border-ink/30 bg-paper-light px-2 py-1.5 font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                        data-testid="admin-roster-type-filter"
                    >
                        {TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <select
                        value={promoted}
                        onChange={(e) => setPromoted(e.target.value)}
                        className="border border-ink/30 bg-paper-light px-2 py-1.5 font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                        data-testid="admin-roster-promoted-filter"
                    >
                        {PROMOTED_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 label-typed">
                    <span data-testid="admin-roster-total">{total} row{total === 1 ? "" : "s"}</span>
                    <span className="text-ink-soft/50">·</span>
                    {TYPE_OPTIONS.filter((o) => o.value).map((o) => (
                        <span key={o.value} data-testid={`admin-roster-by-type-${o.value}`}>
                            {o.label}: {byType[o.value] || 0}
                        </span>
                    ))}
                </div>
            </div>

            {err && (
                <div className="label-typed text-stamp-red mb-3" data-testid="admin-roster-error">
                    // {err}
                </div>
            )}

            <div className="paper-card tape-corner p-3 overflow-x-auto" data-testid="admin-roster-table-card">
                <table className="w-full" data-testid="admin-roster-table">
                    <thead>
                        <tr className="border-b-2 border-ink/30 text-left">
                            <th className="py-2 pr-3 label-typed">#</th>
                            <th className="py-2 pr-3 label-typed">Agent</th>
                            <th className="py-2 pr-3 label-typed">Type</th>
                            <th className="py-2 pr-3 label-typed">Trust · Status</th>
                            <th className="py-2 pr-3 label-typed">Role</th>
                            <th className="py-2 pr-3 label-typed w-[240px]">Tags</th>
                            <th className="py-2 pr-3 label-typed">Promote</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="py-8">
                                    <div className="flex items-center gap-2 text-ink-soft label-typed">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Reading the roster…
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="py-8 text-center label-typed text-ink-soft">
                                    No rows match those filters.
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => (
                            <tr
                                key={r.key}
                                className="border-b border-ink/15 align-top hover:bg-paper-light/40"
                                data-testid={`admin-roster-row-${r.key}`}
                            >
                                <td className="py-3 pr-3 font-heading text-stamp-red text-base tracking-[0.05em] whitespace-nowrap">
                                    {r.number ?? "—"}
                                </td>
                                <td className="py-3 pr-3">
                                    <div className="font-heading uppercase text-sm text-ink tracking-[0.05em]">
                                        {r.name}
                                    </div>
                                    {r.alias && (
                                        <div className="label-typed mt-0.5 text-ink-soft">{r.alias}</div>
                                    )}
                                    <div className="label-typed mt-0.5 text-ink-soft/60">{r.key}</div>
                                </td>
                                <td className="py-3 pr-3">
                                    <TypePill value={r.type} />
                                </td>
                                <td className="py-3 pr-3 whitespace-nowrap">
                                    <div className="label-typed text-ink">
                                        Trust · {typeof r.trusted === "boolean"
                                            ? (r.trusted ? "Yes" : "No")
                                            : (r.trusted || "—")}
                                    </div>
                                    <div className="label-typed text-ink-soft mt-0.5">
                                        {r.status || "—"}
                                    </div>
                                </td>
                                <td className="py-3 pr-3 font-body text-xs text-ink leading-snug max-w-[320px]">
                                    {r.role || <span className="label-typed text-ink-soft/60 italic">no role on file</span>}
                                </td>
                                <td className="py-3 pr-3">
                                    <TagsCell entry={r} onSave={(body) => patchRow(r.key, body)} />
                                </td>
                                <td className="py-3 pr-3">
                                    <PromoteToggle entry={r} onSave={(body) => patchRow(r.key, body)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between" data-testid="admin-roster-pager">
                <div className="label-typed text-ink-soft">
                    Page {pages ? page : 0} of {pages}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || loading}
                        className="border border-ink/30 px-2 py-1 label-typed hover:bg-paper-light disabled:opacity-40"
                        data-testid="admin-roster-prev-page"
                    >
                        <ChevronLeft className="w-4 h-4 inline -mt-0.5" /> Prev
                    </button>
                    <button
                        onClick={() => setPage((p) => Math.min(pages || 1, p + 1))}
                        disabled={page >= pages || loading}
                        className="border border-ink/30 px-2 py-1 label-typed hover:bg-paper-light disabled:opacity-40"
                        data-testid="admin-roster-next-page"
                    >
                        Next <ChevronRight className="w-4 h-4 inline -mt-0.5" />
                    </button>
                </div>
            </div>
        </Shell>
    );
}
